"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Modal } from "react-bootstrap";
import Footer from "../Base/Footer";
import Head from "../Base/Head";
import ApiService from "../services/ApiService";
import { toast } from "react-hot-toast";
import { AddToCalendarButton } from "add-to-calendar-button-react";
import RSAHeaderImg from "./rsa-header.png";

import { isPersonalEmail } from "../utils";

const customFieldLabels = {
  "glean-sydney": { Email: "Official Email" },
};

/* =========================================================
   SINGLE SOURCE OF TRUTH: Signup Options (cards + checkboxes)
   Keys here are the only keys used throughout the file.
========================================================= */
const SIGNUP_OPTIONS = [
  {
    key: "main_stage_program",
    title: "Main Stage Program",
    time: "9:00am – 1:00pm",
    checkboxSummary:
      "9:00am – Welcome, Registration, Coffee\n10:00am – 11:30 – Panel + Keynotes\n11:45am – 1:00pm – Networking Buffet Lunch",
    descriptionLines: [
      "9:00am – Welcome, Registration, Coffee",
      "10:00am – 11:30 – Panel Discussion, Keynotes around future of cybersecurity and enterprise technology including Agentic AI & Security, Quantum Security",
      "11:45am – 1:00pm – Networking Buffet Lunch",
    ],
  },
  {
    key: "showcase_investment",
    title: "Innovation Showcase: Investment Leaders",
    time: "1:30pm – 2:30pm",
    checkboxSummary:
      "1:30pm – 2:30pm\nSignature “speed networking” connecting 10 investment leaders with 10 startup CEOs",
    description:
      "The Innovation Showcase is our signature “speed networking” platform that connects a group of 10 Venture capital and investment leaders with a select set of 10 Hot Startup/CEOs with the goal of promoting relationship building, partnership development and discovery. The format is speed networking, round-robin 5 minute meetings, followed by an opportunity to connect with your corporate peers.",
  },
  {
    key: "showcase_cyber",
    title: "Innovation Showcase: Cybersecurity Leaders",
    time: "3:00pm – 4:00pm",
    checkboxSummary:
      "3:00pm – 4:00pm\nSignature “speed networking” connecting 10 tech executives with a curated set of presenting Israeli technology companies",
    description:
      "The Innovation Showcase is our signature “speed networking” platform that connects a group of 10 Tech Executives with a select set of 10 curated selection of presenting Israeli technology companies to foster meaningful conversations around emerging technologies, partnership opportunities, and real-world security challenges. The format is speed networking, round-robin 5 minute meetings, followed by an opportunity to connect with your corporate peers.",
  },
  {
    key: "pre_scheduled_b2b",
    title: "Pre-Scheduled B2B Meetings",
    time: "1:00pm – 5:00pm",
    checkboxSummary: "1:00pm – 5:00pm\nRequest 1-on-1 meetings with participating companies",
    description: (
      <>
        Throughout the afternoon, attendees will have access to pre-scheduled 1-on-1
        meetings with leading Israeli innovators and technology founders. These private
        conversations offer a unique opportunity to explore cutting-edge solutions,
        discuss potential partnerships, and gain firsthand insight into emerging
        technologies.
        <br />
        <br />
        To request meetings with participating companies, please register using{" "}
        <a
          href="https://israel-innovation-summit-rsa.forms-wizard.biz/"
          target="_blank"
          rel="noreferrer"
        >
          https://israel-innovation-summit-rsa.forms-wizard.biz/
        </a>{" "}
        and enter the complimentary registration code: <strong>INTLAND26</strong>
      </>
    ),
  },
];

/* =========================================================
   Map frontend option keys -> backend option IDs
   (kept stable; only keys changed)
========================================================= */
const OPTION_ID_MAP = {
  main_stage_program: "a3876d39", // was main_session
  showcase_investment: "1181a84f", // was showcase_1
  showcase_cyber: "7b7cce5f", // was showcase_2
  pre_scheduled_b2b: "a3a71476", // was one_to_one (replace later if needed)
};

const GLOBAL_EVENT_ID_OVERRIDE = "757c8601";

const FaqItem = ({
  question,
  answer,
  faqIndex,
  activeFaqIndex,
  setActiveFaqIndex,
  pageContent,
}) => {
  const isFaqSelected = activeFaqIndex === faqIndex;
  const isFirstFaq = faqIndex === 0;

  const handleFaqClick = () => {
    setActiveFaqIndex(isFaqSelected ? null : faqIndex);
  };

  const formattedAnswer = answer.replace(
    /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
    '<a href="mailto:$1">$1</a>'
  );

  return (
    <div
      className={`faq ${isFirstFaq ? "first" : ""}`}
      style={{
        "--theme-primary-color": pageContent.themePrimaryColor || "#015637",
      }}
    >
      <div
        onClick={handleFaqClick}
        className={`faq-head ${isFaqSelected ? "active" : ""}`}
      >
        <div>{question}</div>
      </div>
      <div className={`faq-body ${isFaqSelected ? "active" : ""}`}>
        <p dangerouslySetInnerHTML={{ __html: formattedAnswer }} />
      </div>
    </div>
  );
};

const RsvpField = ({
  className = "",
  fieldLabel,
  inputType = "text",
  value,
  setValue,
  required,
  onBlur = () => {},
}) => {
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (!required) {
      setIsValid(true);
    } else if (required && `${value}`.trim().length) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [value, required]);

  return (
    <div className={`rsvp-field ${className}`}>
      <div className="rsvp-field-label">
        {fieldLabel}
        {required ? <span className="asterisk">*</span> : null}
      </div>
      {inputType !== "textarea" ? (
        <input
          type={inputType}
          className="form-control"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onBlur={onBlur}
        />
      ) : (
        <textarea
          rows="2"
          className="form-control"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onBlur={onBlur}
        />
      )}
    </div>
  );
};

const PartnerHeaderLogos = ({ headerLogos, className = "" }) => {
  const maxWidth = 156;
  const scaleMaxWidth = (scale) => {
    if (scale === 100) return maxWidth;
    return Math.round((scale / 100) * maxWidth);
  };

  return (
    <>
      {headerLogos.map((hl, i) => {
        const style = { maxWidth: `${scaleMaxWidth(hl.scale)}px` };
        if (hl.yOffset && hl.yOffset !== 0) {
          style.transform = `translateY(${hl.yOffset}px)`;
        }
        return (
          <div key={i} className={`header-logo ${className}`} style={style}>
            <img className="img-fluid" src={hl.src} />
          </div>
        );
      })}
    </>
  );
};

const EventDetails = ({
  motionTransisition,
  motionVariants,
  pageContent,
  registrationClosed,

  // RSVP state + handlers
  rsvpSubmitted,
  calendarOptions,
  submitRsvp,
  formValid,
  formErrorMsg,

  // fields
  emailFieldLabel,
  email,
  setEmail,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  jobTitle,
  setJobTitle,
  company,
  setCompany,
  dietaryRestrictions,
  setDietaryRestrictions,
  customAnswer,
  setCustomAnswer,

  // options
  event,
  marketingOptIn,
  marketingOptInChange,
  handleEmailBlur,

  selectedEventOptions,
  toggleEventOption,
}) => {
  return (
    <motion.div
      className="ed-event-info"
      initial="initial"
      animate="animate"
      transition={motionTransisition}
      variants={motionVariants}
    >
      <div className="event-inner">
        <div className="event-image">
          <img className="img-fluid" src={pageContent.eventImages[0]} />
        </div>

        <div className="event-info-container">
          <div className="event-details-heading">EVENT DETAILS</div>

          <div className="event-info">
            <div className="event-info-text">
              <span className="start">Location:</span>
              <br />
              <p>{pageContent.eventLocationFull}</p>
            </div>
            <div className="event-info-text">
              <span className="start">Date:</span>
              {pageContent.eventDate}
            </div>
            <div className="event-info-text">
              <span className="start">Time:</span>
              {pageContent.eventTime}
            </div>
          </div>

          {registrationClosed ? (
            <div className="reg-closed-small">Registration is closed</div>
          ) : rsvpSubmitted ? (
            <>
              <div className="rsvp-modal-title sm">Thank you for your RSVP</div>
              <div className="rsvp-submitted-text">
                {pageContent.afterSubmittedText}
              </div>

              {calendarOptions &&
              calendarOptions.showAddToCalendarButton &&
              calendarOptions.eventProkey ? (
                <div className="add-to-cal-button-lib">
                  <AddToCalendarButton
                    proKey={calendarOptions.eventProkey}
                  ></AddToCalendarButton>
                </div>
              ) : null}
            </>
          ) : (
            <div className="giis-rsvp-form">
              {/* ✅ Checkboxes rendered from single source of truth */}
              <div className="giis-event-option-checkboxes">
                {SIGNUP_OPTIONS.map((opt) => (
                  <label key={opt.key} className="giis-event-option-row">
                    <input
                      type="checkbox"
                      checked={!!selectedEventOptions?.[opt.key]}
                      onChange={() => toggleEventOption(opt.key)}
                    />
                    <div className="giis-event-option-text">
                      <div className="title">{opt.title}</div>
                      <div className="summary">
                        {(opt.checkboxSummary || "").split("\n").map((line, i) => (
                          <React.Fragment key={i}>
                            {line}
                            {i < (opt.checkboxSummary || "").split("\n").length - 1 ? <br /> : null}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              <RsvpField
                className="first"
                fieldLabel={emailFieldLabel}
                inputType="email"
                value={email}
                setValue={setEmail}
                required={true}
                onBlur={handleEmailBlur}
              />

              <div className="fields-row">
                <RsvpField
                  fieldLabel="First Name"
                  value={firstName}
                  setValue={setFirstName}
                  required={true}
                  halfWidth={true}
                />
                <RsvpField
                  fieldLabel="Last Name"
                  value={lastName}
                  setValue={setLastName}
                  required={true}
                  halfWidth={true}
                />
              </div>

              <RsvpField
                fieldLabel="Job Title"
                value={jobTitle}
                setValue={setJobTitle}
                required={true}
              />

              <RsvpField
                className={`${
                  !pageContent.showCustomInput && pageContent.hideDietaryInput
                    ? "last"
                    : ""
                }`}
                fieldLabel="Company Name"
                value={company}
                setValue={setCompany}
                required={true}
              />

              {pageContent.showCustomInput ? (
                <RsvpField
                  className={`${pageContent.hideDietaryInput ? "last" : ""}`}
                  fieldLabel={pageContent.customInputLabel}
                  value={customAnswer}
                  setValue={setCustomAnswer}
                  required={pageContent.customInputRequired}
                />
              ) : null}

              {pageContent.hideDietaryInput ? null : (
                <RsvpField
                  className="last"
                  fieldLabel="Dietary Restrictions"
                  inputType="textarea"
                  value={dietaryRestrictions}
                  setValue={setDietaryRestrictions}
                />
              )}

              {event.googlePolicyOptions ? (
                <label
                  htmlFor="marketingOptIn"
                  className="check rsvp-checkbox-container"
                >
                  <div className="check__box">
                    <input
                      type="checkbox"
                      id="marketingOptIn"
                      checked={marketingOptIn}
                      onChange={marketingOptInChange}
                    />
                    <span>
                      <img
                        src={
                          process.env.PUBLIC_URL +
                          "/images/icons/tick-white.svg"
                        }
                        alt="icon"
                      />
                    </span>
                  </div>
                  <div className="check-label">
                    Sign me up to receive news, product updates, event
                    information and special offers from Google Cloud and its
                    Partners. (optional)
                  </div>
                </label>
              ) : null}

              {event.rubrikPolicyOptions ? (
                <label
                  htmlFor="marketingOptIn"
                  className="check rsvp-checkbox-container"
                >
                  <div className="check__box">
                    <input
                      type="checkbox"
                      id="marketingOptIn"
                      checked={marketingOptIn}
                      onChange={marketingOptInChange}
                    />
                    <span>
                      <img
                        src={
                          process.env.PUBLIC_URL +
                          "/images/icons/tick-white.svg"
                        }
                        alt="icon"
                      />
                    </span>
                  </div>
                  <div className="check-label">
                    Sign me up to receive news, product updates, event
                    information and special offers from Rubrik. (optional)
                  </div>
                </label>
              ) : null}

              <div
                className={`giis-register-button ${formValid ? "" : "disabled"}`}
                onClick={() => submitRsvp()}
                style={{
                  "--theme-primary-color":
                    pageContent.themePrimaryColor || `#61999d`,
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.filter = "brightness(93%)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.filter = "brightness(100%)")
                }
              >
                Register
              </div>

              {!formValid ? (
                <div className="required-field-msg bottom">{formErrorMsg}</div>
              ) : (
                <div className="required-field-msg bottom"></div>
              )}

              {event.googlePolicyOptions ? (
                <div className="check-label legal-notice">
                  I understand my personal data will be processed by Google in
                  accordance with{" "}
                  <a
                    className="inline-link"
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Google’s Privacy Policy
                  </a>{" "}
                  {event.pageUrl.toLowerCase().includes("mistral") ? (
                    <>
                      and{" "}
                      <a
                        className="inline-link"
                        href="https://mistral.ai/terms#privacy-policy"
                        target="_blank"
                        rel="noreferrer"
                      >
                        Mistral's Privacy Policy
                      </a>{" "}
                    </>
                  ) : null}
                  and may be shared with members of{" "}
                  <a
                    className="inline-link"
                    href="https://cloud.google.com/partners"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Google Cloud’s Partner Program
                  </a>
                  , who may send you marketing communications in accordance with
                  the partner's privacy policy.
                </div>
              ) : null}

              {event.rubrikPolicyOptions ? (
                <div className="check-label legal-notice">
                  I understand my personal data will be processed by Rubrik in
                  accordance with{" "}
                  <a
                    className="inline-link"
                    href="https://www.rubrik.com/legal/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Rubrik's Privacy Policy
                  </a>
                  .
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const SignupOptions = ({ pageContent }) => {
  return (
    <div className="giis-signup-options">
      <div className="signup-title">Apply to join us:</div>

      {/* Subtitle not used right now */}
      <div className="signup-subtitle">
        Participants may choose to attend the full day or select individual portions of the agenda that best align with their interests and availability. Please review the agenda below and indicate which sessions you would like to join when applying.
      </div>

      <div className="signup-grid">
        {SIGNUP_OPTIONS.map((opt) => (
          <div key={opt.key} className="signup-card">
            <div className="card-head">
              <div className="card-title">{opt.title}</div>
              {opt.time ? <div className="card-time alt">{opt.time}</div> : null}
            </div>

            {/* Subtitle not used right now */}
            {/* {opt.subtitle ? (
              <div className="card-subtitle">{opt.subtitle}</div>
            ) : null} */}

            {opt.descriptionLines?.length ? (
              <div className="card-desc">
                {opt.descriptionLines.map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>
           ) : opt.description ? (
  <div className="card-desc">
    {typeof opt.description === "string" ? opt.description : opt.description}
  </div>
) : null}

            {opt.agenda?.length ? (
              <div className="option-agenda">
                {opt.agenda.map((row, i) => (
                  <div key={i} className="agenda-row">
                    <div className="time">{row.time}</div>
                    <div className="detail">{row.detail}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function DynamicEvent({ event = {}, referral = {} }) {
  const [activeFaqIndex, setActiveFaqIndex] = useState(null);

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [dietaryRestrictions, setDietaryRestrictions] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  const [customAnswer, setCustomAnswer] = useState("");

  const [rsvpSubmitted, setRsvpSubmitted] = useState(false);
  const [apolloEmailChecked, setApolloEmailChecked] = useState("");

  const [rsvpModal, setRsvpModal] = useState(false);
  const [modalShowClass, setModalShowClass] = useState("");

  const { pageContent, registrationClosed, calendarOptions } = event;

  // ✅ State generated from SIGNUP_OPTIONS keys (single source)
  const [selectedEventOptions, setSelectedEventOptions] = useState(() =>
    SIGNUP_OPTIONS.reduce((acc, opt) => {
      acc[opt.key] = false;
      return acc;
    }, {})
  );

  const getSelectedOptionIds = () => {
    return Object.entries(selectedEventOptions)
      .filter(([, isSelected]) => !!isSelected)
      .map(([key]) => OPTION_ID_MAP[key])
      .filter(Boolean);
  };

  const toggleEventOption = (key) => {
    setSelectedEventOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const textClass = pageContent?.textClass || "";
  const headerTextClass = pageContent?.headerTextClass || "";

  useEffect(() => {
    if (pageContent?.heroHeading) document.title = pageContent.heroHeading;
  }, [pageContent?.heroHeading]);

  useEffect(() => {
    if (rsvpSubmitted) {
      setRsvpModal(true);
    }
  }, [rsvpSubmitted]);

  const marketingOptInChange = (e) => {
    setMarketingOptIn(!!e.target.checked);
  };

  const titleFormattedHeroHeading = (pageContent?.heroHeading || "")
    .replace(/\\n/g, " - ")
    .replace(/\n/g, " - ");

  const head = {
    title: titleFormattedHeroHeading,
    description: pageContent?.heroSubHeading || "",
    url: `https://events.landmarkventures.com/${event.pageUrl}`,
    image: "",
  };

  const subtleBounceTransition = {
    type: "spring",
    stiffness: 96,
    damping: 28,
    duration: 2,
  };

  const headerVariants = {
    initial: { y: -140, opacity: 0 },
    animate: { y: 0, opacity: 1 },
  };

  const infoVariants = {
    initial: { x: -700, opacity: 0 },
    animate: { x: 0, opacity: 1 },
  };

  const imageVariants = {
    initial: { x: 700, opacity: 0 },
    animate: { x: 0, opacity: 1 },
  };

  // Validate form
  let formValid = false;
  let formErrorMsg = ``;

  const validateForm = () => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

    if (!email.length || !emailRegex.test(email)) {
      formErrorMsg = "Please enter a valid email address";
      return false;
    }
    if (pageContent?.businessEmailsOnly && isPersonalEmail(email)) {
      formErrorMsg = "Please use your business email address";
      return false;
    }
    if (!firstName.length) {
      formErrorMsg = "Please enter your first name";
      return false;
    }
    if (!lastName.length) {
      formErrorMsg = "Please enter your last name";
      return false;
    }
    if (!jobTitle.length) {
      formErrorMsg = "Please enter your job title";
      return false;
    }
    if (!company.length) {
      formErrorMsg = "Please enter your company";
      return false;
    }

    const selectedOptionIds = getSelectedOptionIds();
    if (!selectedOptionIds.length) {
      formErrorMsg = "Please select at least one event option";
      return false;
    }

    if (pageContent?.showCustomInput && pageContent?.customInputRequired) {
      if (!customAnswer.length) {
        formErrorMsg = `Please answer the ${pageContent.customInputLabel} question`;
        return false;
      }
    }

    return true;
  };

  formValid = validateForm();

  async function handleEmailBlur() {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!email.length || !emailRegex.test(email)) return false;

    if (
      apolloEmailChecked.toLowerCase().trim() === email.toLowerCase().trim()
    ) {
      return;
    }

    if (event.apolloAutofill) {
      try {
        setApolloEmailChecked(email);
        const api = new ApiService();
        const response = await api.get(
          `landmark/apollo/searchByEmail/${email.trim()}`
        );

        if (response.success && response.data) {
          const { first_name, last_name, organization_name, title } =
            response.data;

          setCompany(organization_name);
          setJobTitle(title);
          setFirstName(first_name);
          setLastName(last_name);
        } else {
          throw "Email search error";
        }
      } catch (error) {
        console.error("handleEmailBlur() - Error searching email", error);
      }
    }
  }

  async function submitRsvp() {
    if (!formValid) {
      console.log(`submitRsvp() - form is invalid`);
      return;
    }

    const selectedOptionIds = getSelectedOptionIds();

    const form = {
      options: selectedOptionIds,
      firstName,
      lastName,
      company,
      jobTitle,
      email,

      dietaryRestrictions: dietaryRestrictions || "",
      businessGoals: pageContent?.showCustomInput ? customAnswer : "",

      referredBy:
        referral && (referral.name || referral.url)
          ? referral.name || referral.url
          : "",

      partners: [],
      eventId: GLOBAL_EVENT_ID_OVERRIDE,
      optionRequests: selectedOptionIds,
    };

    console.log(`submitRsvp()`, form);

    try {
      const api = new ApiService();
      const response = await api.post(`landmark/events/submitGlobalEventRsvp`, {
        form,
      });

      console.log(`submitRsvp() - response`, response);

      if (response.success) {
        setTimeout(() => {
          setRsvpSubmitted(true);
          setRsvpModal(true);
        }, 50);
      } else {
        throw "Backend error";
      }
    } catch (error) {
      console.error("submitRsvp() - Error submitting form", error);
      toast.error("An error occurred. Please try again later.");
    }
  }

  async function handleSetRsvpModal(show) {
    setRsvpModal(!!show);
  }

  let formattedHeroHeading = (pageContent?.heroHeading || "")
    .replace(/\\n/g, "<br /><span>")
    .replace(/\n/g, "<br /><span>");
  if (formattedHeroHeading.includes("<br /><span>")) {
    formattedHeroHeading += "</span>";
  }

  const emailFieldLabel =
    customFieldLabels[event.pageUrl]?.Email || "Business Email";

  return (
    <div className="giis-wrapper branding">
      {/* <Head head={head} noTwitterCard={true} /> */}

      <div className={`ed-wrapper ${textClass}`}>
        <div className="ed-main">
          <motion.div
            className="header-bar"
            initial="initial"
            animate="animate"
            transition={subtleBounceTransition}
            variants={headerVariants}
          >
            <div className="auto__container">
              <div className="header-container">
                <div className="header-logos left">
                  <PartnerHeaderLogos headerLogos={pageContent.headerLogos} />
                  <div className="header-logo lg mob-only">
                    <img
                      className="img-fluid "
                      src={
                        process.env.PUBLIC_URL +
                        "/images/dynamic/landmark-logo-alt.png"
                      }
                      alt="Landmark Ventures"
                    />
                  </div>
                </div>
                <div className="header-logos right">
                  <div className="header-logo lg">
                    <img
                      className="img-fluid"
                      src={
                        process.env.PUBLIC_URL +
                        "/images/dynamic/landmark-logo-alt.png"
                      }
                      alt="Landmark Ventures"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="auto__container">
            <div className="ed-block-container">
              <div className="ed-vertical-flex">
                <div className="ed-left-container">
                  <div className="ed-hero">
                    <motion.div
                      className="hero-info-container"
                      initial="initial"
                      animate="animate"
                      transition={subtleBounceTransition}
                      variants={infoVariants}
                    >
                      {/* <div className="hero-location">
                        <img
                          src={
                            process.env.PUBLIC_URL +
                            "/images/dynamic/location-pointer.svg"
                          }
                          loading="lazy"
                          alt=""
                        />
                        <div>{pageContent.eventLocationShort}</div>
                      </div> */}

                      {/* <h1 className={`hero-heading giis-hero-heading ${headerTextClass}`}>
                        <span className="giis-line giis-line-1">THE GLOBAL-ISRAEL</span>{" "}
                        <span className="giis-line giis-line-2">INNOVATION</span>{" "}
                        <span className="giis-line giis-line-3">SUMMIT AT</span>{" "}
                        <span className="giis-line giis-line-4">RSA</span>
                      </h1> */}

                    <div className="giis-hero-heading-image">
                      <img
                        src={RSAHeaderImg}
                        alt="The Global-Israel Innovation Summit at RSA"
                      />
                    </div>

                      {registrationClosed ? (
                        <div className="reg-closed-big-container">
                          <div className="reg-closed-divider top"></div>
                          <div className="reg-closed-big">
                            Registration is now closed. Thank you for your
                            interest!
                          </div>
                          <div className="reg-closed-divider bottom"></div>
                        </div>
                      ) : null}

                      <div className="hero-subheading">
                        {pageContent.heroSubHeading}
                      </div>

                      <div className="hero-subheading agenda">Summit Agenda:</div>

                      {pageContent.expectBullets &&
                      pageContent.expectBullets.length ? (
                        <ul className="expect-bullets">
                          {pageContent.expectBullets.map((b, i) => (
                            <li key={i}>{b}</li>
                          ))}
                        </ul>
                      ) : null}
                  <SignupOptions pageContent={pageContent} />
                    </motion.div>
                  </div>


                  {/* Mobile card (contains form) */}
                  <div className="w-100 mob-only">
                    <EventDetails
                      motionTransisition={subtleBounceTransition}
                      motionVariants={imageVariants}
                      pageContent={pageContent}
                      registrationClosed={registrationClosed}
                      rsvpSubmitted={rsvpSubmitted}
                      calendarOptions={calendarOptions}
                      submitRsvp={submitRsvp}
                      formValid={formValid}
                      formErrorMsg={formErrorMsg}
                      emailFieldLabel={emailFieldLabel}
                      email={email}
                      setEmail={setEmail}
                      firstName={firstName}
                      setFirstName={setFirstName}
                      lastName={lastName}
                      setLastName={setLastName}
                      jobTitle={jobTitle}
                      setJobTitle={setJobTitle}
                      company={company}
                      setCompany={setCompany}
                      dietaryRestrictions={dietaryRestrictions}
                      setDietaryRestrictions={setDietaryRestrictions}
                      customAnswer={customAnswer}
                      setCustomAnswer={setCustomAnswer}
                      event={event}
                      marketingOptIn={marketingOptIn}
                      marketingOptInChange={marketingOptInChange}
                      handleEmailBlur={handleEmailBlur}
                      selectedEventOptions={selectedEventOptions}
                      toggleEventOption={toggleEventOption}
                    />
                  </div>

                  <motion.div
                    className="ed-expect"
                    initial="initial"
                    animate="animate"
                    transition={{ ...subtleBounceTransition, delay: 0.2 }}
                    variants={infoVariants}
                  >
                    <div className="horizontal-rule"></div>

                    {pageContent.faqs && pageContent.faqs.length ? (
                      <div className="ed-faqs">
                        <div className="ed-faq-title section-heading sm">
                          FAQs
                        </div>
                        {pageContent.faqs.map((faq, i) => (
                          <FaqItem
                            key={i}
                            question={faq.question}
                            answer={faq.answer}
                            faqIndex={i}
                            activeFaqIndex={activeFaqIndex}
                            setActiveFaqIndex={setActiveFaqIndex}
                            pageContent={pageContent}
                          />
                        ))}
                      </div>
                    ) : null}
                  </motion.div>
                </div>

                {/* Desktop right rail (contains form) */}
                <div className="ed-right-rail">
                  <div className="sticky-content">
                    <EventDetails
                      motionTransisition={subtleBounceTransition}
                      motionVariants={imageVariants}
                      pageContent={pageContent}
                      registrationClosed={registrationClosed}
                      rsvpSubmitted={rsvpSubmitted}
                      calendarOptions={calendarOptions}
                      submitRsvp={submitRsvp}
                      formValid={formValid}
                      formErrorMsg={formErrorMsg}
                      emailFieldLabel={emailFieldLabel}
                      email={email}
                      setEmail={setEmail}
                      firstName={firstName}
                      setFirstName={setFirstName}
                      lastName={lastName}
                      setLastName={setLastName}
                      jobTitle={jobTitle}
                      setJobTitle={setJobTitle}
                      company={company}
                      setCompany={setCompany}
                      dietaryRestrictions={dietaryRestrictions}
                      setDietaryRestrictions={setDietaryRestrictions}
                      customAnswer={customAnswer}
                      setCustomAnswer={setCustomAnswer}
                      event={event}
                      marketingOptIn={marketingOptIn}
                      marketingOptInChange={marketingOptInChange}
                      handleEmailBlur={handleEmailBlur}
                      selectedEventOptions={selectedEventOptions}
                      toggleEventOption={toggleEventOption}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {pageContent.footerImages && pageContent.footerImages.length ? (
            <div className="auto__container">
              <div className="footer-image-outer">
                <div className="footer-image">
                  <img
                    className="img-fluid"
                    src={pageContent.footerImages[0]}
                  />
                </div>
              </div>
            </div>
          ) : null}

          <div className="footer-spacer"></div>

          {!pageContent.hideLandmarkFooter ? (
            <div className="ed-footer">
              <Footer alternate={true} />
            </div>
          ) : null}
        </div>
      </div>

      {/* Post-submit modal */}
      <Modal
        className={`modal fade rsvp-modal ${modalShowClass}`}
        centered
        show={rsvpModal && rsvpSubmitted}
        onHide={() => {
          setModalShowClass("scale-out");
          handleSetRsvpModal(false);
        }}
        onShow={() => {
          setModalShowClass("scale-in");
        }}
        dialogClassName="scale-in"
      >
        {rsvpSubmitted ? (
          <>
            <div
              className="rsvp-modal-close"
              onClick={() => handleSetRsvpModal(false)}
            >
              X
            </div>
            <motion.div
              initial="initial"
              animate="animate"
              transition={{
                duration: 0.4,
                ease: [0.25, 0.1, 0.25, 1],
              }}
            >
              <div className="rsvp-modal-title sm">Thank you for your RSVP</div>
              <div className="rsvp-submitted-text">
                {pageContent.afterSubmittedText}
              </div>
              {calendarOptions &&
              calendarOptions.showAddToCalendarButton &&
              calendarOptions.eventProkey ? (
                <div className="add-to-cal-button-lib">
                  <AddToCalendarButton
                    proKey={calendarOptions.eventProkey}
                  ></AddToCalendarButton>
                </div>
              ) : null}
            </motion.div>{" "}
          </>
        ) : null}
      </Modal>
    </div>
  );
}
